/*
 * HeroHim storefront analytics — the single client tracking entry point.
 *
 * Loaded as a plain <script defer> by snippets/posthog.liquid (which has already
 * emitted window.HHAnalytics.{config,context} synchronously and booted the
 * PostHog stub). NOTHING else in the theme should call posthog.capture() — every
 * event routes through track()/trackBeacon() here so the sanitize pass and the
 * never-throw guard always apply (mirrors the quiz app's useAnalytics() rule).
 *
 * Owns the pre-cart funnel: product_viewed, variant_selected,
 * add_to_cart(_clicked/_failed), faq_opened, cta_clicked, section_viewed,
 * collection_viewed, search_submitted, product_left, labra_outbound_click,
 * labra_package_cta_click.
 * The checkout funnel lives in the Shopify
 * custom web pixel (docs/PostHog-analytics/shopify-customer-pixel/). Identity bridge: the
 * anonymous distinct_id — and, since 26.8.2026, the PostHog session id with a
 * freshness stamp — are written into Shopify cart attributes on add_to_cart so
 * the (cross-origin, sandboxed) pixel can stitch browse → checkout.
 *
 * See docs/PostHog-analytics/posthog-shopify-implementation-plan.md.
 */
(() => {
  // Idempotent — the snippet is in <head>, but guard anyway (matches scroll-reveal.js).
  if (globalThis.__hhAnalyticsInit) return;
  globalThis.__hhAnalyticsInit = true;

  const HH = globalThis.HHAnalytics;
  if (!HH || !HH.config) return; // inert when the project key is blank

  const ph = () => globalThis.posthog;
  const ctx = () => HH.context || {};

  // ---- PII sanitize (layer 2; the SDK property_denylist is layer 1) ----------
  // Strips by KEY before anything leaves the page. cart_token / order_id survive
  // (they are pseudonymous ids, not "token"/"name"/etc.).
  const FORBIDDEN_KEYS = [
    'email', 'first_name', 'last_name', 'name', 'phone', 'address',
    'password', 'token', 'customer', 'note',
  ];
  const sanitize = (props) => {
    const out = {};
    for (const k of Object.keys(props || {})) {
      if (FORBIDDEN_KEYS.includes(k)) continue;
      if (props[k] === undefined) continue;
      out[k] = props[k];
    }
    return out;
  };

  // ---- track wrappers (never throw into the page / checkout) ------------------
  let reentrant = false;
  const track = (event, props) => {
    try {
      const merged = Object.assign({ page_type: ctx().page_type }, props || {});
      ph()?.capture(event, sanitize(merged));
    } catch (err) {
      if (reentrant) return;
      reentrant = true;
      try {
        ph()?.capture('app_error', { error_class: (err && err.name) || 'Error' });
      } catch (_) {
        /* swallow */
      }
      reentrant = false;
    }
  };
  // For events fired right before unload — sendBeacon + send_instantly or the
  // event sits in the batch queue and is dropped on navigation.
  const trackBeacon = (event, props) => {
    try {
      const merged = Object.assign({ page_type: ctx().page_type }, props || {});
      ph()?.capture(event, sanitize(merged), { transport: 'sendBeacon', send_instantly: true });
    } catch (_) {
      /* best-effort */
    }
  };

  // ---- shared prop builders --------------------------------------------------
  const productBaseProps = () => {
    const c = ctx();
    return {
      product_handle: c.product_handle,
      product_id: c.product_id,
      product_title: c.product_title,
      product_type: c.product_type,
      variant_id: c.variant_id,
      price: c.price,
      compare_at_price: c.compare_at_price,
      currency: c.currency,
      is_subscription: c.is_subscription,
      purchase_type: c.purchase_type,
      tier: c.tier,
      vertical: c.vertical,
      template_kind: c.template_kind,
      template_suffix: c.template_suffix,
    };
  };

  // ---- internal-traffic flag (?internal=1 / 0) -------------------------------
  // Resolving the flag, persisting it (localStorage + a mirrored one-year
  // cookie) and registering the `is_internal` super property all moved INTO
  // snippets/posthog.liquid, synchronously, so the flag also lands on the
  // automatic $pageview — which fires before any deferred script runs, and so
  // used to go out unflagged on the very page load that did the flagging. See
  // the comment block there. This file only reads the resolved value, for the
  // cart-attribute bridge; the localStorage read stays as a fallback so the
  // function is still correct on its own.
  const INTERNAL_LS = 'hh_internal';
  const isInternal = () => {
    if (typeof HH.isInternal === 'boolean') return HH.isInternal;
    try {
      return globalThis.localStorage?.getItem(INTERNAL_LS) === '1';
    } catch (_) {
      return false;
    }
  };

  // ---- identity bridge: distinct_id → Shopify cart attributes ----------------
  // Written on add_to_cart (the first moment a cart exists). The sandboxed
  // checkout pixel reads init.data.cart.attributes.hh_did to attach checkout
  // events to the same anonymous person. One POST also returns the cart token,
  // which we stamp on add_to_cart as a property-level correlation backup.
  // PostHog persists the distinct_id in localStorage under the key derived from
  // persistence_name 'phs' (snippets/posthog.liquid) — `ph_phs`. While the async
  // array.js stub hasn't upgraded to the real library yet, get_distinct_id() returns
  // undefined; read the persisted id directly so a fast (or slow-network) add-to-cart
  // still stitches to the same person rather than skipping the bridge write entirely.
  const readPersistedDid = () => {
    try {
      const ls = globalThis.localStorage;
      if (!ls) return null;
      const didFrom = (raw) => {
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed.distinct_id === 'string' && parsed.distinct_id) || null;
      };
      // Expected key first; fall back to scanning any ph_* persistence object so this
      // stays correct if the persistence_name / key shape ever changes.
      const direct = didFrom(ls.getItem('ph_phs'));
      if (direct) return direct;
      for (let i = 0; i < ls.length; i += 1) {
        const key = ls.key(i);
        if (!key || key.indexOf('ph_') !== 0) continue;
        const did = didFrom(ls.getItem(key));
        if (did) return did;
      }
      return null;
    } catch (_) {
      return null;
    }
  };
  const getDid = () => {
    try {
      const live = ph()?.get_distinct_id?.();
      if (live) return live;
    } catch (_) {
      /* fall through to the persisted-id fallback */
    }
    return readPersistedDid();
  };

  // ---- session bridge: $session_id → Shopify cart attributes -----------------
  // The checkout pixel POSTs straight to the capture endpoint (no posthog-js,
  // sandboxed worker, Shopify-controlled origin), so it has no session context
  // of its own: every checkout_* event arrived with $session_id NULL, which
  // makes any session-scoped funnel report zero checkouts. Nothing about that
  // is fixable pixel-side — it cannot read this origin's storage — so the
  // session id rides the same cart attributes the distinct_id already uses.
  //
  // Written as ONE attribute, `hh_sid` = "<session_id>~<epoch_ms>", to keep the
  // order's note_attributes from growing another line. The timestamp is the
  // freshness proof: PostHog ends a session after 30 minutes idle, so a
  // checkout that starts later belongs to a DIFFERENT session and the pixel
  // must not stamp this id on it. The pixel enforces that window; see
  // docs/PostHog-analytics/shopify-customer-pixel/. Failing the check leaves
  // $session_id unset — exactly today's behaviour, never a fabricated session.
  //
  // Re-written on every add-to-cart, so the timestamp is as fresh as the last
  // real cart action.
  const readPersistedSid = () => {
    try {
      const ls = globalThis.localStorage;
      if (!ls) return null;
      // posthog-js persists the session under $sesid as
      // [lastActivityTs, sessionId, sessionStartTs] in the same object the
      // distinct_id lives in (persistence_name 'phs' -> key `ph_phs`).
      const raw = ls.getItem('ph_phs');
      if (!raw) return null;
      const sesid = JSON.parse(raw)?.$sesid;
      return (Array.isArray(sesid) && typeof sesid[1] === 'string' && sesid[1]) || null;
    } catch (_) {
      return null;
    }
  };
  const getSid = () => {
    try {
      const live = ph()?.get_session_id?.();
      if (live) return live;
    } catch (_) {
      /* the array.js stub returns undefined until it upgrades */
    }
    return readPersistedSid();
  };

  const bridgeAndEmitAdd = (detail) => {
    const did = getDid();
    const internal = isInternal() ? '1' : '0';
    const sid = getSid();
    // '' deletes the attribute rather than storing an empty one, so a page
    // where the session id is unknowable leaves no misleading residue.
    const sidAttr = sid ? `${sid}~${Date.now()}` : '';
    const emit = (token) => {
      track('add_to_cart', Object.assign(productBaseProps(), {
        item_count: detail?.data?.itemCount ?? null,
        variant_id: detail?.sourceId || ctx().variant_id,
        cta_location: lastCtaLocation || 'buybox',
        cart_token: token || null,
      }));
      lastCtaLocation = null;
    };
    // Same-origin cookie: a cheap optimization for any same-origin pixel context.
    try {
      if (did) {
        document.cookie = `_hh_ph=${encodeURIComponent(did)}; path=/; max-age=2592000; SameSite=Lax`;
      }
    } catch (_) {
      /* ignore */
    }
    if (!did) {
      emit(null);
      return;
    }
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { hh_did: did, hh_internal: internal, hh_sid: sidAttr } }),
      keepalive: true,
    })
      .then((r) => r.json())
      .then((cart) => emit(cart && cart.token))
      .catch(() => emit(null));
  };

  // ---- search-term PII guard (the one deliberately-kept free-text field) ------
  const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
  const LONGDIGIT_RE = /\d{6,}/;
  const guardSearch = (term) => {
    if (!term) return null;
    if (EMAIL_RE.test(term) || LONGDIGIT_RE.test(term)) return null;
    return term;
  };

  // ---- CTA helpers -----------------------------------------------------------
  let lastCtaLocation = null;
  const ATC_SELECTOR =
    '.ppg-atc, .ppr-atc, .pla-atc, [data-ppg-submit], [data-ppr-submit], [data-pla-submit]';
  const ctaLocationFor = (btn) => {
    if (btn.matches('.ppg-atc, .ppr-atc, .pla-atc')) return 'buybox';
    if (btn.closest('.ppg-sticky, .ppr-sticky, .pla-sticky')) return 'sticky';
    if (btn.closest('.ppg-closing, .ppr-closing, .pla-closing')) return 'closing';
    return 'buybox';
  };
  // Secondary CTAs. product-program uses modifier classes on one shared element
  // (--doctor / --lab / --arrow, where --arrow is the "lighter option"
  // up/down-tier cross-link); product-retail has no secondary CTAs, so those only
  // ever match on program pages. The landing pages name theirs individually:
  // .pla-faq__cta is the in-FAQ doctor-booking anchor on
  // /products/painonhallinnan-perusta, [data-pla-jump] is its "katso koko
  // sisältö" jump down to the components band, and .pla-exit__cta is the
  // soft-exit coda's link to the free kartoitus — the only non-purchase
  // conversion that page offers, so it is the one to watch against
  // add_to_cart_clicked.
  const SECONDARY_CTA_SELECTOR =
    '[class*="ppg-secondary__cta"], .pla-faq__cta, [data-pla-jump], .pla-exit__cta';
  const ctaTypeFor = (el) => {
    if (el.matches('.pla-faq__cta')) return 'doctor';
    if (el.matches('[data-pla-jump]')) return 'contents_jump';
    if (el.matches('.pla-exit__cta')) return 'kartoitus';
    const c = el.className || '';
    if (/--doctor/.test(c)) return 'doctor';
    if (/--lab/.test(c)) return 'lab';
    if (/--arrow/.test(c)) return 'tier';
    return 'other';
  };
  const classifyCartError = (msg, errors) => {
    // product-form.js flags a transport failure (offline, flaky mobile, aborted
    // request) as `errors.network` — it never reached the cart, so it is not the
    // same funnel event as a rejection the cart actually answered.
    if (errors && errors.network === true) return 'network_error';
    const m = (msg || '').toLowerCase();
    if (m.includes('stock') || m.includes('sold') || m.includes('varast') || m.includes('loppu')) {
      return 'out_of_stock';
    }
    return 'cart_error';
  };

  // ---- Labra outbound handoff ------------------------------------------------
  // /pages/labrat-tilaus hands the visitor off to Labrakeskus, who take the
  // payment — the funnel goes dark at that anchor. This is the only measurement
  // of it we have, and it is the baseline the gift modal will be judged against
  // (see docs/labra-gift-flow-plan.md §9.2). Wired on every page type: it costs
  // one delegated listener and only ever matches the four Labrakeskus anchors.
  //
  // trackBeacon, not track — the click navigates away, so a batched capture is
  // dropped before it flushes.
  const LABRA_HOST = 'tilaa.labrakeskus.fi';
  const wireLabraOutbound = () => {
    document.addEventListener('click', (e) => {
      const a = e.target?.closest?.('a[href]');
      if (!a) return;
      let host;
      try {
        host = new URL(a.href, globalThis.location.href).host;
      } catch (_) {
        return;
      }
      if (host !== LABRA_HOST) return;
      // The gift modal intercepts this same click in the CAPTURE phase and
      // calls preventDefault(), so by the time this bubble-phase listener runs
      // the flag is already set and no navigation is happening. Counting it
      // here would inflate the very baseline the modal is judged against
      // (plan §9.2). The modal re-fires the event itself, with via:'modal',
      // at the moment it actually hands the visitor over.
      if (e.defaultPrevented) return;
      trackBeacon('labra_outbound_click', {
        // Set by the section's synchronous <html data-paketti> script.
        paketti: document.documentElement.dataset.paketti || null,
        cta_location: a.closest('.lt-sticky') ? 'sticky' : 'inline',
        via: 'direct',
      });
    });
  };

  // ---- Labra package CTA (/pages/labrat -> /pages/labrat-tilaus) -------------
  // The step BEFORE the hand-off, and until 8.9.2026 the only unmeasured one in
  // the flow: a plain internal <a href> that the CTA selectors above never
  // matched, because they are scoped to the .ppg-/.ppr-/.pla- product-program
  // classes these cards do not carry. Only the $pageview on the destination
  // showed it, which cannot distinguish "chose a package" from "arrived from a
  // link somewhere else".
  //
  // Two card shapes, one meaning: the pricecards (.ll-cta-stack__btn) and the
  // decide cards further down (.ll-decide__cta). Both are merchant-editable
  // hrefs whose defaults point at the order page but which MAY be pointed
  // straight at Labrakeskus — so `destination` records which, rather than the
  // event pretending to know. When it is external, wireLabraOutbound above also
  // fires labra_outbound_click; the two are different facts about one click, not
  // a double count.
  //
  // trackBeacon, not track — the click navigates away.
  const LABRA_CTA_SELECTOR = '.ll-cta-stack__btn, .ll-decide__cta';
  const wireLabraPackageCta = () => {
    document.addEventListener('click', (e) => {
      const a = e.target?.closest?.(LABRA_CTA_SELECTOR);
      if (!a) return;
      if (e.defaultPrevented) return;
      let url;
      try {
        url = new URL(a.href, globalThis.location.href);
      } catch (_) {
        return;
      }
      // Published Shopify package links have no query; retain the legacy query
      // and pricecard modifier fallbacks for old and merchant-configured links.
      const productPath = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname;
      const fromProduct = productPath.endsWith('/products/herolab-med-lahjakortti')
        ? 'med'
        : productPath.endsWith('/products/herolab-max-lahjakortti') ? 'max' : null;
      const fromQuery = url.searchParams.get('paketti');
      const fromClass = /--max\b/.test(a.className || '') ? 'max' : /--med\b/.test(a.className || '') ? 'med' : null;
      trackBeacon('labra_package_cta_click', {
        paketti: fromProduct || (fromQuery === 'max' || fromQuery === 'med' ? fromQuery : fromClass),
        cta_location: a.matches('.ll-decide__cta') ? 'decide' : 'pricecard',
        destination: url.host === LABRA_HOST ? 'external' : fromProduct ? 'product_page' : 'order_page',
      });
    });
  };

  // ---- Labra gift modal ------------------------------------------------------
  // The modal lives in sections/labrat-tilaus.liquid and owns its own DOM and
  // choreography; it reports here by dispatching CustomEvents on document, so
  // this file stays the only caller of posthog.capture() (see the header rule).
  //
  //   hh:labra-gift      detail { name: shown|submitted|skipped|dismissed|consent_blocked, … }
  //   hh:labra-outbound  detail { paketti, cta_location, via }
  //
  // No PII crosses this boundary: the modal never puts the email in the detail,
  // and sanitize() would strip an `email` key anyway.
  //
  // `consent_blocked` — they filled the email but would not tick the tips
  // subscription, which has been the condition of the gift since 6.8.2026. It
  // is the price of that decision, so it is worth counting against `submitted`.
  const GIFT_EVENTS = new Set([
    'shown',
    'submitted',
    'skipped',
    'dismissed',
    'consent_blocked',
  ]);
  const wireLabraGift = () => {
    document.addEventListener('hh:labra-gift', (e) => {
      const d = e?.detail || {};
      if (!GIFT_EVENTS.has(d.name)) return;
      const props = {
        paketti: d.paketti || document.documentElement.dataset.paketti || null,
        cta_location: d.cta_location || null,
        // Which capture surface: 'modal' (/pages/labrat-tilaus) or 'labralahja'
        // (the standing claim page). Older cached pages send no source.
        source: d.source || null,
      };
      if (d.name === 'submitted') {
        props.marketing_consent = d.marketing_consent === true;
        props.result = d.result || null;
      }
      if (d.name === 'dismissed') props.method = d.method || null;
      // submitted/skipped precede a handoff and need the beacon; the rest stay
      // on the page.
      const emit = d.name === 'submitted' || d.name === 'skipped' ? trackBeacon : track;
      emit(`labra_gift_${d.name}`, props);
    });

    document.addEventListener('hh:labra-outbound', (e) => {
      const d = e?.detail || {};
      trackBeacon('labra_outbound_click', {
        paketti: d.paketti || document.documentElement.dataset.paketti || null,
        cta_location: d.cta_location || null,
        via: d.via || 'modal',
      });
    });
  };

  // ---- page-load events ------------------------------------------------------
  const firePageLoad = () => {
    const c = ctx();
    if (c.page_type === 'product') {
      track('product_viewed', productBaseProps());
    } else if (c.page_type === 'collection') {
      track('collection_viewed', {
        collection_handle: c.collection_handle,
        collection_title: c.collection_title,
        product_count: c.product_count,
      });
    } else if (c.page_type === 'search') {
      track('search_submitted', {
        search_term: guardSearch(c.search_term),
        results_count: c.results_count ?? 0,
        has_results: (c.results_count || 0) > 0,
      });
    }
  };

  // ---- listeners (delegated on document) -------------------------------------
  // Wired only on product/collection/search pages so these never fire on the
  // homepage / FAQ page / account pages (which also use .hh-faq__trigger etc.).
  const wireListeners = () => {
    // Add to cart — success: cart:update from the product form, not an error and
    // not a cart-line edit (which use source 'cart-items-component').
    document.addEventListener('cart:update', (e) => {
      const d = e?.detail;
      if (d?.data?.source !== 'product-form-component') return;
      // A failed add dispatches BOTH cart:update{didError:true} and cart:error
      // (see assets/product-form.js). Let the cart:error handler own the failure
      // so add_to_cart_failed isn't counted twice — here we only skip it.
      if (d?.data?.didError === true) return;
      bridgeAndEmitAdd(d);
    });

    document.addEventListener('cart:error', (e) => {
      track('add_to_cart_failed', Object.assign(productBaseProps(), {
        error_class: classifyCartError(e?.detail?.data?.message, e?.detail?.data?.errors),
      }));
    });

    // Add-to-cart intent (records which button for the next success).
    document.addEventListener('click', (e) => {
      const btn = e.target?.closest?.(ATC_SELECTOR);
      if (!btn) return;
      lastCtaLocation = ctaLocationFor(btn);
      track('add_to_cart_clicked', Object.assign(productBaseProps(), { cta_location: lastCtaLocation }));
    });

    // Secondary CTAs (doctor booking, labs, tier cross-link, contents jump).
    document.addEventListener('click', (e) => {
      const cta = e.target?.closest?.(SECONDARY_CTA_SELECTOR);
      if (!cta) return;
      const ctaType = ctaTypeFor(cta);
      // Anything that navigates gets the beacon: a batched capture is dropped
      // before it flushes on unload — same reason labra_outbound_click uses it.
      // That covers the doctor booking (external flow) and the kartoitus coda
      // (another Shopify page). The contents jump is the one exception — it
      // stays on the page, so it can go through the normal batch.
      const emit = ctaType === 'contents_jump' ? track : trackBeacon;
      emit('cta_clicked', {
        cta_type: ctaType,
        // null on the jump: it is a <button>, not an anchor.
        href: cta.getAttribute('href'),
        product_handle: ctx().product_handle,
      });
    });

    // FAQ — capture phase so we read aria-expanded BEFORE heroapp-faq.js flips it.
    document.addEventListener(
      'click',
      (e) => {
        const trig = e.target?.closest?.('.hh-faq__trigger');
        if (!trig) return;
        if (trig.getAttribute('aria-expanded') !== 'false') return; // only count opening
        const q = trig.querySelector('.hh-faq__question');
        track('faq_opened', {
          question: q ? q.textContent.trim() : null,
          product_handle: ctx().product_handle,
        });
      },
      true,
    );

    // Variant selection. (detail.sourceId on the add event / detail.resource.id
    // here are Horizon product-form/variant-picker contracts — see assets/events.js.)
    document.addEventListener('variant:selected', (e) => {
      track('variant_selected', {
        variant_id: e?.detail?.resource?.id || ctx().variant_id,
        product_handle: ctx().product_handle,
        vertical: ctx().vertical, // dashboard "Varianttivalinnat" breaks down by this
      });
    });

    // Combined-listing swap: a new product loaded → refresh the stale context handle.
    document.addEventListener('variant:update', (e) => {
      try {
        const np = e?.detail?.data?.newProduct;
        if (np?.url && HH.context) {
          HH.context.product_handle = np.url.split('?')[0].split('/').pop();
          HH.context.variant_id = null; // unknown until the next variant:selected
        }
      } catch (_) {
        /* ignore */
      }
    });
  };

  // ---- engagement depth (product pages only) ---------------------------------
  const wireEngagement = () => {
    if (ctx().page_type !== 'product') return;
    let maxScroll = 0;
    let left = false;
    const start = globalThis.performance?.now ? performance.now() : null;
    const onScroll = () => {
      const h = document.documentElement;
      const scrollable = h.scrollHeight - h.clientHeight;
      if (scrollable <= 0) return;
      const top = h.scrollTop || globalThis.scrollY || 0;
      const pct = Math.min(100, Math.round((top / scrollable) * 100));
      if (pct > maxScroll) maxScroll = pct;
    };
    globalThis.addEventListener('scroll', onScroll, { passive: true });
    const leave = () => {
      if (left) return;
      left = true;
      const secs = start != null ? Math.round((performance.now() - start) / 1000) : null;
      trackBeacon('product_left', Object.assign(productBaseProps(), {
        max_scroll_depth_pct: maxScroll,
        time_on_page_sec: secs,
      }));
    };
    globalThis.addEventListener('pagehide', leave);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') leave();
    });
  };

  // ---- band depth: section_viewed --------------------------------------------
  // One observer for the whole document, generic over any page that marks its
  // bands up with data-hh-band="<name>". product_left's max_scroll_depth_pct
  // answers *how far* a visitor got; this answers *which argument* they reached,
  // which is the only version of the question a long landing page can act on.
  // Fires at most once per band per page view (Set-guarded, and the element is
  // unobserved as soon as it counts).
  //
  // A band counts as reached on EITHER test below: half the band is on screen,
  // or the band covers half the viewport. The second is what carries the long
  // bands, since a band taller than the viewport can never reach ratio 0.5.
  //
  // The threshold list has to be a fine ramp, not [0, 0.5], and that is the
  // whole subtlety here. IntersectionObserver only calls back when a threshold
  // is CROSSED. For a band taller than 2x the viewport the ratio peaks at
  // vh/height < 0.5, so the 0.5 crossing never happens; the only callback is the
  // 0 crossing on entry, and at that instant intersectionRect.height is a few
  // pixels, so the viewport-coverage test cannot rescue it either. The band then
  // emits nothing at all and reads as drop-off. Worse, it fails asymmetrically:
  // the long bands clear 2x the viewport on a phone but not on a desktop, so the
  // funnel silently under-reports exactly the mobile paid traffic it exists to
  // measure. A 0.01 ramp gives callbacks all the way through the scroll, so the
  // coverage test is actually evaluated while the band fills the screen.
  const BAND_THRESHOLDS = Array.from({ length: 101 }, (_, i) => i / 100);
  const seenBands = new Set();
  let bandObserver = null;
  const onBandIntersect = (entries) => {
    // Hoisted out of the loop: the 0.01 threshold ramp means up to ~100 callbacks
    // per band, and the viewport height cannot change between entries of one
    // callback. (IntersectionObserver callbacks run post-layout, so this read
    // forces no reflow either way — it is just needless work in the hot path.)
    const vh = globalThis.window.innerHeight || document.documentElement.clientHeight || 0;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const halfOfBandShown = entry.intersectionRatio >= 0.5;
      const bandFillsHalfTheViewport = vh > 0 && entry.intersectionRect.height >= vh * 0.5;
      if (!halfOfBandShown && !bandFillsHalfTheViewport) continue;
      bandObserver?.unobserve(entry.target);
      const band = entry.target.dataset.hhBand;
      if (!band || seenBands.has(band)) continue;
      seenBands.add(band);
      track('section_viewed', {
        band,
        // Both undefined off a product page; sanitize() drops undefined keys.
        product_handle: ctx().product_handle,
        vertical: ctx().vertical,
      });
    }
  };
  const wireBandDepth = () => {
    if (typeof globalThis.IntersectionObserver !== 'function') return;
    const bands = document.querySelectorAll('[data-hh-band]');
    if (bands.length === 0) return;
    bandObserver =
      bandObserver ||
      new globalThis.IntersectionObserver(onBandIntersect, { threshold: BAND_THRESHOLDS });
    // observe() is a no-op for an element already being observed, so re-scanning
    // after a shopify:section:load re-render cannot double-wire anything.
    bands.forEach((el) => bandObserver.observe(el));
  };

  // Retargeting gift (assets/hh-retarget-gift.js). Wired unconditionally, like
  // the labra gift: a retargeting ad can land on any template, so gating this on
  // page_type the way wireListeners() does would silently drop most of it.
  //
  // `added` is deliberately NOT reported as add_to_cart. The visitor does tap
  // for the gift, so the event is genuine — but it is a 0 € item they were
  // handed, not a product they decided to buy, and folding it into the
  // add-to-cart funnel would read as purchase intent that never existed.
  // It lands once, as gift_added.
  const RETARGET_GIFT_EVENTS = new Set([
    'shown',
    'added',
    'dismissed',
    'removed',
    'clamped',
    'blocked',
    'deferred',
    'open_cart',
  ]);
  const wireRetargetGift = () => {
    document.addEventListener('hh:retarget-gift', (e) => {
      const d = e?.detail || {};
      if (!RETARGET_GIFT_EVENTS.has(d.name)) return;
      track(`gift_${d.name}`, {
        // `reason` distinguishes an out-of-stock no-op from a real failure, and
        // a revalidation removal from a customer dismissal.
        reason: d.reason || null,
        gift_variant_id: d.variant_id || null,
      });
    });
  };

  // ---- boot ------------------------------------------------------------------
  firePageLoad();
  wireLabraOutbound();
  wireLabraPackageCta();
  wireLabraGift();
  wireRetargetGift();
  // Not gated on page_type: data-hh-band is opt-in markup, so any template that
  // carries it gets the signal and every other page does one empty querySelectorAll.
  wireBandDepth();
  document.addEventListener('shopify:section:load', wireBandDepth);
  if (['product', 'collection', 'search'].includes(ctx().page_type)) {
    wireListeners();
    wireEngagement();
  }
})();
